<template>
  <div class="cta_analytics">
    <h3 class="cta_header">
      <ui-button iconName="icon--prev-gray-size16" circle hideLabel
                 @click="resetCtaData"/>
      {{ $t('cta.analysis.header.title')}}
    </h3>
    <ReportSegment v-if="reportPeriod.start === reportPeriod.end"
                   :timeSeries="timeSeries"
                   @onDeviceDataByHour="onDeviceDataByHour"/>
    <ui-tab :items="tabNames" :value="isExcept ? 1 : 0"
            @input="onCtaExceptChange($event)"/>
    <content-box class="content_box referer_box" v-if="imageData && coordsData">
      <h4 class="box_title">
        {{ $t('cta.analysis.content.cta.title', { targetLabel }) }}
      </h4>
      <div class="image_wrap">
        <crop-image class="image_box"
                    ref="imageBox"
                    :class="{ except: isExcept }"
                    :url="imageData.url"
                    :imageWidth="imageData.width"
                    :imageHeight="imageData.height"
                    :width="coordsData.width"
                    :height="coordsData.height"
                    :offset="{ x: coordsData.left, y: coordsData.top }"
                    :maxWidth="300"
                    :maxHeight="100">
          <span v-if="isExcept" class="except-border">
            <svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="none">
              <line x1="0" y1="0" x2="100" :y2="100" style="stroke:rgb(170,170,170); stroke-width:1" />
            </svg>
            <svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="none">
              <line x1="0" :y1="100" :x2="100" y2="0" style="stroke:rgb(170,170,170); stroke-width:1" />
            </svg>
          </span>
        </crop-image>
      </div>
      <p class="referer_info">
        {{ $t('cta.analysis.content.cta.tableTitle', { targetLabel }) }}
      </p>
      <table class="referer_data">
        <thead>
        <tr>
          <th class="rank" width="34">{{ $t('common.rank') }}</th>
          <th class="url">URL</th>
          <th class="rate" width="48">{{ $t('common.table.header.rate') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in rankList" :key="index">
          <td class="rank">{{ index + 1 }}</td>
          <td class="url"><div class="ellipsis">{{ item.refer }}</div></td>
          <td class="rate">{{ item.percentage }}%</td>
        </tr>
        </tbody>
      </table>
    </content-box>

    <div v-if="reportName != 'stream'">
      <div class="chart_box">
        <content-box class="content_box" v-if="pvChartOptions">
          <h4 class="box_title">{{ $t('cta.analysis.content.pv.title') }}</h4>
          <app-chart chartType="line"
            :chartOptions="pvChartOptions"
            :height="180" />
        </content-box>
      </div>

      <div class="chart_box">
        <content-box class="content_box" v-if="uvChartOptions">
          <h4 class="box_title">{{ $t('cta.analysis.content.uv.title') }}</h4>
          <app-chart chartType="line"
                     :chartOptions="uvChartOptions"
                     :height="180"/>
        </content-box>
      </div>

      <div class="chart_box" v-if="!isAMP">
        <content-box class="content_box" v-if="userTypeChartOptions">
          <h4 class="box_title">{{ $t('cta.analysis.content.newReturn.title') }}</h4>
          <app-chart chartType="bar"
                     :chartOptions="userTypeChartOptions"
                     :height="180"/>
        </content-box>
      </div>

      <div class="chart_box" :class="{'amp' : isAMP}">
        <content-box class="content_box" v-if="avgDurationChartOptions">
          <h4 class="box_title">{{ $t('cta.analysis.content.avgDuration.title') }}</h4>
          <app-chart chartType="bar"
                     :chartOptions="avgDurationChartOptions"
                     :height="180"/>
        </content-box>
      </div>
    </div>

  </div>
</template>

<script>
import { parse, format } from 'date-fns'
import { getPercentage, thousand } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import CropImage from '@/components/ui/CropImage'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import UiTab from '@/components/ui/UiTab'
import AppChart from '@/components/chart'
import ReportSegment from '@/components/reporting-heatmaps/ReportSegment.vue'
import { NEW_MOBILE_HEATMAP_STARTDATE } from '@/libs/constants'
import { mapState } from 'vuex'

let tabNames = ['CTA Clicked', 'CTA Unclicked']

export default {
  name: 'CtaAnalytics',
  components: {
    CropImage,
    ContentBox,
    UiButton,
    UiTab,
    AppChart,
    ReportSegment
  },
  props: [
    'ctaData',
    'reportData',
    'reportName',
    'isAMP',
    'timeSeries'
  ],
  data () {
    return {
      tabNames,
      currentTab: tabNames[0]
    }
  },
  created () {
    this.newCheck()
  },
  computed: {
    ...mapState([
      'reportPeriod'
    ]),
    bNewMobile () {
      let bAMP = this.isAMP
      let bNewMobile = true
      if ((this.reportPeriod.start < NEW_MOBILE_HEATMAP_STARTDATE) || bAMP || this.isDesktopDevice) {
        bNewMobile = false
      }
      return bNewMobile
    },
    isExcept () {
      return this.ctaData.except
    },
    targetLabel () {
      let device = this.reportData.device
      if (device !== 'desktop' && this.bNewMobile) {
        return this.isExcept ? 'Untapped' : 'Tapped'
      }
      return this.isExcept ? 'Unclicked' : 'Clicked'
    },
    imageData () {
      if (!this.reportData.image) {
        return null
      }
      return this.reportData.image
    },
    coordsData () {
      if (!this.ctaData.coords) {
        return null
      }
      return this.ctaData.coords
    },
    rawData () {
      return this.reportData.data.ctaData
    },
    rankList () {
      const rawData = this.rawData
      const total = rawData.pvTotal

      return rawData.refer.map(item => {
        return {
          ...item,
          percentage: getPercentage(item.count, total, 1)
        }
      })
    },
    pvChartOptions () {
      let chartType = 'pv'
      switch (this.reportData.eventSource) {
        case 'new':
          chartType = 'newPv'
          break
        case 'return':
          chartType = 'returnPv'
          break
      }
      return this.getChartOptions(chartType)
    },
    uvChartOptions () {
      let chartType = 'uv'
      switch (this.reportData.eventSource) {
        case 'new':
          chartType = 'newUv'
          break
        case 'return':
          chartType = 'returnUv'
          break
      }
      return this.getChartOptions(chartType)
    },
    userTypeChartOptions () {
      return this.getUserTypeChartOptions()
    },
    avgDurationChartOptions () {
      return this.getAvgDurationChartOptions()
    },
    totalUserLabel () {
      return this.$t('cta.analysis.content.chart.totalUser')
    },
    ctaUesrLabel () {
      return this.$t('cta.analysis.content.chart.ctaUser')
    }
  },
  methods: {
    newCheck () {
      let device = this.reportData.device
      if (device !== 'desktop' && this.bNewMobile) {
        this.tabNames = ['CTA Tapped', 'CTA Untapped']
      }
    },
    onCtaExceptChange (value) {
      const data = { ...this.ctaData }
      data.except = value
      this.$emit('onCtaDataChange', data)
    },
    resetCtaData () {
      this.$emit('onCtaDataChange', { coords: null, except: false })
    },
    getChartOptions (type) {
      const dailyDevice = this.reportData.dailyDevice
      const device = this.reportData.device
      const targetUserData = []
      const allUserData = []
      let targetField = type
      switch (this.reportData.eventSource) {
        case 'new':
        case 'return':
          targetField = targetField.replace(this.reportData.eventSource, '').toLowerCase()
          break
      }

      if (this.isExcept) {
        switch (type.replace(this.reportData.eventSource, '').toLowerCase()) {
          case 'pv':
            targetField = 'orgPv'
            break
          case 'uv':
            targetField = 'orgUv'
            break
        }
      }

      for (const dailyItem of dailyDevice) {
        const day = dailyItem.day
        const timestamp = parse(day).getTime()
        const targetItem = this.rawData.daily.find(item => item.day === day)
        let allValue = 0
        let targetValue = 0

        if (dailyItem.device[device]) {
          allValue = dailyItem.device[device][type] || 0
        }

        if (targetItem) {
          if (this.isExcept) {
            if (allValue < targetItem[targetField]) {
              targetValue = 0
            } else {
              targetValue = allValue - targetItem[targetField]
            }
          } else {
            if (allValue < targetItem[targetField]) {
              targetValue = allValue
            } else {
              targetValue = targetItem[targetField]
            }
          }
        }
        targetUserData.push([timestamp, targetValue])
        allUserData.push([timestamp, allValue])
      }

      const options = {
        meta: {
          isDailyChart: true
        },
        series: [
          {
            name: this.targetLabel,
            type: 'line',
            data: targetUserData,
            showSymbol: false,
            symbolSize: 2,
            smooth: true
          },
          {
            name: this.totalUserLabel,
            type: 'line',
            data: allUserData,
            showSymbol: false,
            symbolSize: 2,
            smooth: true
          }
        ],
        xAxis: {
          splitNumber: 6
        },
        tooltip: {
          formatter (params) {
            const title = format(parse(params[0].axisValue), 'YYYY-MM-DD')
            const content = []

            for (let i = 0; i < params.length; i++) {
              const param = params[i]
              content.push([
                `${param.marker}${param.seriesName} :`,
                thousand(param.data[1])
              ])
            }
            return getTooltipHtml(content, title)
          }
        },
        color: ['#ec0047', '#43b6c9']
      }
      return options
    },
    getUserTypeChartOptions () {
      // const infoData = this.reportData.infoData
      const device = this.reportData.device
      const dailyDevice = this.reportData.dailyDevice
      let allUv = 0
      let returnUv = 0
      let newUv = 0

      if (dailyDevice && dailyDevice.length > 0) {
        for (const dailyItem of dailyDevice) {
          if (dailyItem.device[device]) {
            returnUv += dailyItem.device[device].returnUv
            newUv += dailyItem.device[device].newUv
          }
        }
      }

      allUv = returnUv + newUv
      const allNewUvPercentage = getPercentage(newUv, allUv, 1)
      const allRevisitUvPercentage = getPercentage(returnUv, allUv, 1)

      const targetUv = this.rawData.uvTotal
      const targetNewUv = this.rawData.newUvTotal
      const targetRevisitUv = targetUv - targetNewUv
      const targetNewUvPercentage = getPercentage(targetNewUv, targetUv, 1)
      const targetRevisitUvPercentage = getPercentage(targetRevisitUv, targetUv, 1)

      const options = {
        xAxis: {
          type: 'value',
          min: 0,
          max: 100,
          interval: 25,
          axisLabel: {
            formatter: '{value}%'
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#f4f4f1'
            }
          },
          axisTick: {
            show: true
          }
        },
        yAxis: {
          type: 'category',
          data: [this.totalUserLabel, this.targetLabel]
        },
        series: [
          {
            name: this.$t('common.new'),
            type: 'bar',
            barWidth: 10,
            stack: 'stack-0',
            data: [allNewUvPercentage, targetNewUvPercentage],
            itemStyle: {
              barBorderRadius: [5, 0, 0, 5]
            }
          },
          {
            name: this.$t('common.revisit'),
            type: 'bar',
            barWidth: 10,
            stack: 'stack-0',
            data: [allRevisitUvPercentage, targetRevisitUvPercentage],
            itemStyle: {
              barBorderRadius: [0, 5, 5, 0]
            }
          }
        ],
        tooltip: {
          formatter (params) {
            const title = params[0].name
            const content = []

            for (let i = 0; i < params.length; i++) {
              const param = params[i]
              content.push([
                `${param.marker}${param.seriesName} :`,
                `${param.data.toFixed(1)}%`
              ])
            }
            return getTooltipHtml(content, title)
          }
        },
        color: ['#1199af', '#9fce12']
      }
      return options
    },
    getAvgDurationChartOptions () {
      const infoData = this.reportData.infoData
      const device = this.reportData.device
      let targetSessTime = this.rawData.sessTime || 0
      let targetPv = this.rawData.pvTotal || 0
      let allUserSessTime = 0
      let allUserPv = 0

      if (infoData.device && infoData.device[device]) {
        let sessTime = 'sessTime'
        let pv = 'pv'
        switch (this.reportData.eventSource) {
          case 'new':
            sessTime = 'newSessTime'
            pv = 'newPv'
            break
          case 'return':
            sessTime = 'returnSessTime'
            pv = 'returnPv'
            break
        }
        allUserSessTime = infoData.device[device][sessTime]
        allUserPv = infoData.device[device][pv]
      }

      if (targetSessTime) {
        if (this.isExcept) {
          targetSessTime = this.rawData.orgSessTime || 0
          targetPv = this.rawData.orgPvTotal || 0
          targetSessTime = Math.round(((allUserSessTime * allUserPv) - (targetSessTime * targetPv)) / (allUserPv - targetPv) / 1000)
        } else {
          targetSessTime = Math.round(targetSessTime / 1000)
        }
      }

      if (allUserSessTime) {
        allUserSessTime = Math.round(allUserSessTime / 1000)
      }

      const minSecFormat = (ms) => {
        return `${Math.floor(ms / 60)}:${ms % 60}s`
      }

      const options = {
        legend: {
          show: false
        },
        xAxis: {
          type: 'value',
          minInterval: 240,
          splitLine: {
            show: true,
            lineStyle: {
              color: '#f4f4f1'
            }
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            formatter (value, index) {
              return minSecFormat(value)
            }
          }
        },
        yAxis: {
          type: 'category',
          data: [this.totalUserLabel, this.targetLabel]
        },
        series: [
          {
            name: this.$t('cta.analysis.content.avgDuration.title'),
            type: 'bar',
            barWidth: 10,
            data: [
              {
                value: allUserSessTime,
                itemStyle: {
                  color: '#1199af'
                }
              },
              {
                value: targetSessTime,
                itemStyle: {
                  color: '#9fce12'
                }
              }
            ],
            itemStyle: {
              barBorderRadius: [5, 5, 5, 5]
            }
          }
        ],
        tooltip: {
          formatter (params) {
            const param = params[0]
            const title = param.name
            const seriesName = param.seriesName
            const content = [
              [
                `${param.marker}${seriesName} :`,
                minSecFormat(param.value)
              ]
            ]
            return getTooltipHtml(content, title)
          }
        }
      }
      return options
    },
    onDeviceDataByHour (value) {
      this.$emit('onHourData', value)
    }
  }
}
</script>

<style lang="scss">
.cta_analytics {
  .cta_header {
    font-size: 20px;
    font-weight: 500;
    color: #444;
    & > button {
      margin-right: 3px;
      vertical-align: middle;
    }
  }
  .ui_tab {
    margin-top: 20px;
  }
  .content_box {
    padding: 25px;
    margin-top: 30px;

    &.referer_box {
      margin-top: 20px;
    }
  }
  .box_title {
    margin-bottom: 20px;
    font-size: 15px;
    font-weight: 500;
    color: #2f2f2f;
  }
  .image_wrap {
    position: relative;
    height: 112px;
    border-radius: 6px;
    border: solid 1px #d7d7d7;
    background-color: #f4f4f4;

    .image_box {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      outline: 3px solid #ffe310;

      &.except {
        outline: none;
      }
    }

    .except-border {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px solid #aaa;
      svg {
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
  .referer_info {
    font-size: 13px;
    font-weight: 500;
    color: #2f2f2f;
    margin: 40px 0 25px 0;
  }
  .referer_data {
    table-layout: fixed;
    width: 100%;
    margin-bottom: 5px;

    .rank {
      text-align: left;
    }
    .rate {
      text-align: right;
    }

    th {
      font-size: 12px;
      color: #444;
      border-bottom: 1px solid #bbb;
      padding-bottom: 6px;
      box-sizing: border-box;

      &.rank {
        text-align: left;
        padding-left: 2px;
      }
      &.rate {
        text-align: right;
        padding-right: 2px;
      }
    }
    td {
      font-size: 14px;
      line-height: 1.71;
      color: #444444;

      &.rank {
        text-align: center;
      }
      &.url {
        text-align: left;
      }
      &.rate {
        text-align: right;
      }
    }
    tbody > tr:first-child td {
      color: #ec0047;
      padding-top: 8px;
    }
  }
}

@media screen and (min-width: 1790px) {
  .cta_analytics .chart_box {
    &.amp {
      width : 100%;
    }
    display: inline-block;
    width: calc(50% - 15px);

    &:nth-child(2n-1) {
      margin-right: 30px;
    }
  }
}
</style>
